import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users,
  Plus,
  UserPlus,
  Phone,
  DollarSign,
  Trash2,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Search,
  ShieldCheck,
  Trophy,
  Sparkles,
  ChevronRight,
  Shirt,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';
import { ContextualState, PageLoadingState } from '../components/StatePanel';

const Teams = () => {
  const { tournamentId } = useParams();
  const { getAuthHeader, isOrganizer } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [teamForm, setTeamForm] = useState({
    name: '',
    captain_name: '',
    contact_number: '',
    captain_role: 'batsman',
    captain_jersey_number: '',
  });
  const [playerForm, setPlayerForm] = useState({ name: '', role: 'batsman', jersey_number: '' });

  const fetchData = useCallback(
    async (showError = true) => {
      try {
        const [tournamentRes, teamsRes] = await Promise.all([
          axios.get(`${API}/tournaments/${tournamentId}`, getAuthHeader()),
          axios.get(`${API}/tournaments/${tournamentId}/teams`, getAuthHeader()),
        ]);
        setTournament(tournamentRes.data);
        setTeams(teamsRes.data);
      } catch (error) {
        if (showError) {
          toast.error('Failed to load teams');
        }
      } finally {
        setLoading(false);
      }
    },
    [tournamentId, getAuthHeader]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!teamForm.name) {
      toast.error('Team name is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/tournaments/${tournamentId}/teams`,
        {
          ...teamForm,
          captain_jersey_number: teamForm.captain_jersey_number
            ? parseInt(teamForm.captain_jersey_number, 10)
            : null,
        },
        getAuthHeader()
      );
      setTeams([...teams, response.data]);
      setTeamDialogOpen(false);
      setTeamForm({
        name: '',
        captain_name: '',
        contact_number: '',
        captain_role: 'batsman',
        captain_jersey_number: '',
      });
      toast.success('Team created!');
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!playerForm.name || !selectedTeam) {
      toast.error('Player name is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/teams/${selectedTeam.id}/players`,
        {
          ...playerForm,
          jersey_number: playerForm.jersey_number ? parseInt(playerForm.jersey_number, 10) : null,
        },
        getAuthHeader()
      );

      setTeams(
        teams.map((team) => {
          if (team.id === selectedTeam.id) {
            return { ...team, players: [...(team.players || []), response.data] };
          }
          return team;
        })
      );

      setPlayerDialogOpen(false);
      setPlayerForm({ name: '', role: 'batsman', jersey_number: '' });
      toast.success('Player added!');
    } catch (error) {
      toast.error('Failed to add player');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Delete this team and all its players?')) return;

    try {
      await axios.delete(`${API}/teams/${teamId}`, getAuthHeader());
      setTeams(teams.filter((team) => team.id !== teamId));
      toast.success('Team deleted');
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleDeletePlayer = async (teamId, playerId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}/players/${playerId}`, getAuthHeader());
      setTeams(
        teams.map((team) => {
          if (team.id === teamId) {
            return { ...team, players: team.players.filter((player) => player.id !== playerId) };
          }
          return team;
        })
      );
      toast.success('Player removed');
    } catch (error) {
      toast.error('Failed to remove player');
    }
  };

  const handleFeeStatusToggle = async (teamId, currentStatus) => {
    const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
    try {
      await axios.put(
        `${API}/teams/${teamId}/fee-status?status=${newStatus}`,
        {},
        getAuthHeader()
      );
      setTeams(teams.map((team) => (team.id === teamId ? { ...team, fee_status: newStatus } : team)));
      toast.success(`Fee status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update fee status');
    }
  };

  const openAddPlayerDialog = (team) => {
    setSelectedTeam(team);
    setPlayerDialogOpen(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'batsman':
        return 'bg-sky-500/10 text-sky-700 border-sky-500/20';
      case 'bowler':
        return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20';
      case 'all-rounder':
        return 'bg-violet-500/10 text-violet-700 border-violet-500/20';
      case 'wicket-keeper':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      default:
        return 'bg-slate-500/10 text-slate-700 border-slate-500/20';
    }
  };

  const filteredTeams = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) return teams;

    return teams.filter((team) => {
      const matchesTeam =
        team.name?.toLowerCase().includes(normalizedQuery) ||
        team.captain_name?.toLowerCase().includes(normalizedQuery) ||
        team.players?.some((player) => player.name?.toLowerCase().includes(normalizedQuery));

      return matchesTeam;
    });
  }, [teams, searchQuery]);

  const summaryCards = useMemo(() => {
    const totalPlayers = teams.reduce((acc, team) => acc + (team.players?.length || 0), 0);

    return [
      {
        label: 'Registered Teams',
        value: teams.length,
        icon: Users,
        tone: 'border-white/10 bg-white/[0.04]',
      },
      {
        label: 'Squad Players',
        value: totalPlayers,
        icon: Shirt,
        tone: 'border-sky-500/25 bg-sky-500/[0.05] text-sky-100',
      },
      ...(isOrganizer()
        ? [
            {
              label: 'Fees Paid',
              value: teams.filter((team) => team.fee_status === 'paid').length,
              icon: ShieldCheck,
              tone: 'border-emerald-500/25 bg-emerald-500/[0.05] text-emerald-100',
            },
            {
              label: 'Fees Pending',
              value: teams.filter((team) => team.fee_status === 'pending').length,
              icon: DollarSign,
              tone: 'border-amber-500/25 bg-amber-500/[0.05] text-amber-100',
            },
          ]
        : []),
    ];
  }, [teams, isOrganizer]);

  if (loading) {
    return (
      <Layout>
        <PageLoadingState body="list" />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 md:space-y-8">
        <section className="relative overflow-hidden rounded-[32px] border border-white/10 bg-[#08111f] text-white shadow-[0_24px_80px_rgba(3,8,20,0.45)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.22),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(56,189,248,0.18),transparent_30%)]" />
          <div className="relative p-5 md:p-7">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="space-y-4">
                <Link
                  to={`/tournaments/${tournamentId}`}
                  className="inline-flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tournament Hub
                </Link>
                <div className="space-y-3">
                  <Badge className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-white/80">
                    Team Center
                  </Badge>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
                      {isOrganizer() ? 'Build and manage every squad' : 'Explore the teams in this tournament'}
                    </h1>
                    <p className="mt-2 max-w-3xl text-sm text-white/72 md:text-base">
                      {isOrganizer()
                        ? 'Register teams, confirm captains, expand rosters, and keep the tournament lineup ready from one squad board.'
                        : 'Follow registered teams, browse captains and player lists, and stay close to the competition roster.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px] xl:max-w-[420px]">
                <div className="rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-white/60">Tournament</p>
                  <p className="mt-2 text-lg font-semibold">{tournament?.name}</p>
                  <p className="mt-1 text-sm text-white/65">
                    {teams.length} team{teams.length === 1 ? '' : 's'} currently registered
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-emerald-100/70">Roster Readiness</p>
                  <p className="mt-2 text-lg font-semibold text-emerald-50">
                    {teams.length > 0 ? 'Squads are live' : 'Waiting on registrations'}
                  </p>
                  <p className="mt-1 text-sm text-emerald-50/75">
                    {teams.length > 0
                      ? 'Expand any team card to review the full player list and captain setup.'
                      : 'Start by adding the first team so the tournament can move into scheduling.'}
                  </p>
                </div>
              </div>
            </div>

            {isOrganizer() && (
              <div className="mt-5">
                <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="h-11 bg-white text-slate-950 hover:bg-white/90" data-testid="add-team-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Team
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[560px]">
                    <DialogHeader>
                      <DialogTitle>Add Team</DialogTitle>
                      <DialogDescription>
                        Register a team, capture the captain details, and create the initial squad entry.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateTeam} className="mt-4 space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="team-name">Team Name *</Label>
                        <Input
                          id="team-name"
                          value={teamForm.name}
                          onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                          placeholder="Royal Strikers"
                          data-testid="team-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="captain">Captain Name</Label>
                        <Input
                          id="captain"
                          value={teamForm.captain_name}
                          onChange={(e) => setTeamForm({ ...teamForm, captain_name: e.target.value })}
                          placeholder="John Smith"
                          data-testid="captain-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Captain Role</Label>
                          <Select
                            value={teamForm.captain_role}
                            onValueChange={(value) => setTeamForm({ ...teamForm, captain_role: value })}
                          >
                            <SelectTrigger data-testid="captain-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="batsman">Batsman</SelectItem>
                              <SelectItem value="bowler">Bowler</SelectItem>
                              <SelectItem value="all-rounder">All-Rounder</SelectItem>
                              <SelectItem value="wicket-keeper">Wicket Keeper</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="captain-jersey">Captain Jersey Number</Label>
                          <Input
                            id="captain-jersey"
                            type="number"
                            value={teamForm.captain_jersey_number}
                            onChange={(e) =>
                              setTeamForm({ ...teamForm, captain_jersey_number: e.target.value })
                            }
                            placeholder="7"
                            data-testid="captain-jersey-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact">Contact Number</Label>
                        <Input
                          id="contact"
                          value={teamForm.contact_number}
                          onChange={(e) => setTeamForm({ ...teamForm, contact_number: e.target.value })}
                          placeholder="+91 98765 43210"
                          data-testid="contact-input"
                        />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setTeamDialogOpen(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1" data-testid="submit-team-button">
                          Add Team
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </section>

        <section className={cn('grid gap-4', isOrganizer() ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-2')}>
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className={cn('border-white/10 shadow-sm', card.tone)}>
                <CardContent className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {card.label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight">{card.value}</p>
                  </div>
                  <div className="rounded-2xl border border-current/10 bg-background/40 p-3">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="space-y-6">
            <Card className="border-white/10 shadow-sm">
              <CardContent className="space-y-4 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      Squad Board
                    </p>
                    <h2 className="mt-1 text-2xl font-semibold tracking-tight">Registered teams</h2>
                  </div>

                  <div className="relative min-w-[220px] flex-1 lg:max-w-sm lg:flex-none">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search team, captain, or player"
                      className="pl-10"
                    />
                  </div>
                </div>

                {filteredTeams.length === 0 ? (
                  <ContextualState
                    icon={Users}
                    eyebrow="Squad Board"
                    title={teams.length === 0 ? 'No teams registered yet' : 'No teams match this search'}
                    description={
                      teams.length === 0
                        ? isOrganizer()
                          ? 'Add the first team to start building out squads and unlocking schedule creation.'
                          : 'Teams will appear here once the organizer starts registering squads.'
                        : 'Try a different team, captain, or player name to bring those squads back into view.'
                    }
                    action={
                      teams.length === 0 && isOrganizer() ? (
                        <Button onClick={() => setTeamDialogOpen(true)} className="gap-2">
                          <Plus className="h-4 w-4" />
                          Add First Team
                        </Button>
                      ) : null
                    }
                  />
                ) : (
                  <div className="space-y-4" data-testid="teams-list">
                    {filteredTeams.map((team, index) => (
                      <TeamCard
                        key={team.id}
                        team={team}
                        index={index}
                        isOrganizer={isOrganizer()}
                        expanded={expandedTeam === team.id}
                        onToggleExpand={() =>
                          setExpandedTeam(expandedTeam === team.id ? null : team.id)
                        }
                        onAddPlayer={() => openAddPlayerDialog(team)}
                        onDeleteTeam={() => handleDeleteTeam(team.id)}
                        onDeletePlayer={(playerId) => handleDeletePlayer(team.id, playerId)}
                        onToggleFeeStatus={() => handleFeeStatusToggle(team.id, team.fee_status)}
                        getRoleColor={getRoleColor}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-white/10 bg-[#091222] text-white shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-cyan-300" />
                  Squad Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-white/72">
                <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/55">Captain setup</p>
                  <p className="mt-2 text-base font-semibold text-white">
                    {teams.filter((team) => Boolean(team.captain_name)).length} teams have a captain assigned
                  </p>
                  <p className="mt-1">
                    Captains are automatically created as players when added during team registration.
                  </p>
                </div>

                <div className="space-y-3 rounded-2xl border border-white/10 bg-white/6 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/55">Squad cues</p>
                  <div className="space-y-3">
                    <InsightRow
                      icon={Users}
                      title="Expandable team cards"
                      description="Open any squad to review all registered players without leaving the roster board."
                    />
                    <InsightRow
                      icon={UserPlus}
                      title="Rapid player adds"
                      description="Organizers can add new players directly from the team card where roster context is visible."
                    />
                    <InsightRow
                      icon={ShieldCheck}
                      title="Admin-only controls"
                      description="Fee status and contact controls stay hidden from viewers while admins retain full management access."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <QuickLink
                  to={`/tournaments/${tournamentId}`}
                  title="Tournament hub"
                  description="Return to the main control view for this competition."
                />
                <QuickLink
                  to={`/tournaments/${tournamentId}/matches`}
                  title="Fixtures"
                  description="Move from squad setup into schedule and match management."
                />
                <QuickLink
                  to={`/tournaments/${tournamentId}/player-stats`}
                  title="Player stats"
                  description="Review top performers and player leaderboard trends."
                />
              </CardContent>
            </Card>
          </div>
        </section>

        {isOrganizer() && (
          <Dialog open={playerDialogOpen} onOpenChange={setPlayerDialogOpen}>
            <DialogContent className="sm:max-w-[520px]">
              <DialogHeader>
                <DialogTitle>Add Player to {selectedTeam?.name}</DialogTitle>
                <DialogDescription>Add a new player to the team roster</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddPlayer} className="mt-4 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="player-name">Player Name *</Label>
                  <Input
                    id="player-name"
                    value={playerForm.name}
                    onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })}
                    placeholder="Virat Singh"
                    data-testid="player-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select
                      value={playerForm.role}
                      onValueChange={(value) => setPlayerForm({ ...playerForm, role: value })}
                    >
                      <SelectTrigger data-testid="player-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="batsman">Batsman</SelectItem>
                        <SelectItem value="bowler">Bowler</SelectItem>
                        <SelectItem value="all-rounder">All-Rounder</SelectItem>
                        <SelectItem value="wicket-keeper">Wicket Keeper</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="jersey">Jersey Number</Label>
                    <Input
                      id="jersey"
                      type="number"
                      value={playerForm.jersey_number}
                      onChange={(e) => setPlayerForm({ ...playerForm, jersey_number: e.target.value })}
                      placeholder="7"
                      data-testid="player-jersey-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPlayerDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" data-testid="submit-player-button">
                    Add Player
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

const TeamCard = ({
  team,
  index,
  isOrganizer,
  expanded,
  onToggleExpand,
  onAddPlayer,
  onDeleteTeam,
  onDeletePlayer,
  onToggleFeeStatus,
  getRoleColor,
}) => (
  <Card
    className="border-white/10 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md"
    style={{ animationDelay: `${index * 50}ms` }}
    data-testid={`team-card-${team.id}`}
  >
    <CardContent className="p-4 md:p-5">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <button
            type="button"
            className="flex flex-1 items-start gap-4 text-left"
            onClick={onToggleExpand}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-xl font-semibold tracking-tight">{team.name}</h3>
                {team.captain_name && (
                  <Badge variant="outline" className="rounded-full px-2.5 py-1">
                    Captain: {team.captain_name}
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                <span>{team.players?.length || 0} players</span>
                {team.players?.some((player) => player.is_captain) && (
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Captain synced to squad
                  </span>
                )}
              </div>
            </div>
          </button>

          <div className="flex flex-wrap items-center gap-2">
            {isOrganizer() ? (
              <>
                <Button
                  variant={team.fee_status === 'paid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={onToggleFeeStatus}
                  className={cn(team.fee_status === 'paid' && 'bg-emerald-600 hover:bg-emerald-700')}
                  data-testid={`fee-status-${team.id}`}
                >
                  <DollarSign className="mr-1 h-3.5 w-3.5" />
                  {team.fee_status === 'paid' ? 'Paid' : 'Pending'}
                </Button>
                <Button variant="outline" size="sm" onClick={onAddPlayer} data-testid={`add-player-${team.id}`}>
                  <UserPlus className="mr-1.5 h-4 w-4" />
                  Add Player
                </Button>
                <Button variant="ghost" size="icon" onClick={onDeleteTeam} data-testid={`delete-team-${team.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </>
            ) : null}
            <Button variant="ghost" size="icon" onClick={onToggleExpand}>
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Squad Size</p>
            <p className="mt-2 text-2xl font-semibold">{team.players?.length || 0}</p>
          </div>
          <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Captain</p>
            <p className="mt-2 text-base font-semibold">{team.captain_name || 'Not assigned'}</p>
          </div>
          <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Roster State</p>
            <p className="mt-2 text-base font-semibold">
              {team.players?.length ? 'Players registered' : 'Waiting for first player'}
            </p>
          </div>
        </div>

        {expanded && (
          <div className="rounded-3xl border border-border/70 bg-muted/20 p-4 md:p-5">
            {isOrganizer && team.contact_number && (
              <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4" />
                {team.contact_number}
              </div>
            )}

            {team.players?.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-background/70 px-4 py-10 text-center">
                <p className="text-sm text-muted-foreground">No players added yet</p>
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {team.players?.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-start justify-between rounded-2xl border border-border/70 bg-background/80 p-4"
                    data-testid={`player-${player.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {player.jersey_number ? (
                        <div className="rounded-xl border border-border bg-muted/60 px-2.5 py-2 text-sm font-semibold text-muted-foreground">
                          #{player.jersey_number}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-border bg-muted/50 p-2 text-muted-foreground">
                          <Shirt className="h-4 w-4" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">
                          {player.name}
                          {player.is_captain && <span className="ml-2 text-primary">(C)</span>}
                        </p>
                        <Badge variant="outline" className={cn('mt-2 text-xs', getRoleColor(player.role))}>
                          {player.role}
                        </Badge>
                      </div>
                    </div>

                    {isOrganizer && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onDeletePlayer(player.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const InsightRow = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="rounded-xl border border-white/10 bg-white/8 p-2">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div>
      <p className="font-medium text-white">{title}</p>
      <p className="mt-1 text-sm text-white/65">{description}</p>
    </div>
  </div>
);

const QuickLink = ({ to, title, description }) => (
  <Link
    to={to}
    className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 transition hover:border-primary/30 hover:bg-primary/[0.04]"
  >
    <div>
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground" />
  </Link>
);

export default Teams;
